/**
 * First-Run-Onboarding — Orientierungs-Tour (driver.js Spotlight).
 *
 * NUR stabile Shell-Anker (Sidebar/Topbar), die im Layout auf jeder authed
 * Route + bei leerer Library präsent sind. Transiente Features (Bulk-Bar,
 * Quick-Look, Sharing, Tagging, Delete-Undo) sind beim First-Run NICHT im DOM
 * → die kommen als kontextuelle Tipps (PR2, `use-contextual-tip`), nicht hier.
 *
 * `startTour` ist bzgl. Backend **side-effect-frei**: es ruft nur `onEnd` (der
 * Caller entscheidet, ob `POST /onboarding/complete`). So markiert ein manueller
 * Restart (ohne `onEnd`) das Flag NICHT erneut.
 *
 * Die Driver-Factory ist injizierbar → Trigger/Callback-Logik in node/jsdom
 * testbar, ohne echte Spotlight-Geometrie (jsdom hat kein Layout).
 *
 * driver.js (+ CSS) wird erst beim ersten echten Start per `import()` geladen —
 * NICHT statisch: sonst landet es im DAM-Entry-Chunk (~26 kB, `bundle:check`).
 */
import type { Config, Driver, DriveStep } from 'driver.js';

export interface TourStepDef {
  /** `data-tour`-Key des Ziel-Elements. Fehlt → zentrierter Step (z.B. Welcome). */
  anchor?: string;
  title: string;
  description: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

/** Orientierungs-Tour — stabile Shell-Anker (siehe data-tour in Sidebar/Topbar). */
export const ORIENTATION_STEPS: TourStepDef[] = [
  {
    title: 'Willkommen im GREENFORCE DAM 👋',
    description:
      'Hier liegen alle Bilder, Grafiken und Videos zentral. Eine kurze Tour zeigt dir, wo was ist — du kannst jederzeit „Überspringen“.',
  },
  {
    anchor: 'nav-library',
    title: 'Deine Mediathek',
    description: 'Alle Assets als Grid — durchsuchbar, filterbar, nach Kampagnen gruppiert.',
    side: 'right',
    align: 'start',
  },
  {
    anchor: 'search',
    title: 'Schnell finden',
    description: 'Tippe Dateiname, Tag oder Kampagne — die Suche ist unscharf-tolerant.',
    side: 'bottom',
    align: 'start',
  },
  {
    anchor: 'upload-button',
    title: 'Hochladen',
    description: 'Neue Assets per Klick oder Drag-&-Drop. Der Upload läuft auch beim Weiterklicken im Hintergrund.',
    side: 'bottom',
    align: 'end',
  },
  {
    anchor: 'nav-recipes',
    title: 'Rezepte & Collections',
    description: 'Gruppiere Assets zu Alben (Rezepte) oder kuratierten Sets (Collections) — teilbar als Galerie-Link.',
    side: 'right',
    align: 'start',
  },
  {
    anchor: 'nav-settings',
    title: 'Einstellungen & Profil',
    description: 'Profil, Team und Vokabular. Diese Tour kannst du hier jederzeit neu starten.',
    side: 'right',
    align: 'start',
  },
];

/** Injizierbare driver.js-Factory (Default = echtes driver.js). Test-Seam. */
export type DriverFactory = (config: Config) => Driver;
async function loadDefaultFactory(): Promise<DriverFactory> {
  const [{ driver }] = await Promise.all([
    import('driver.js'),
    import('driver.js/dist/driver.css'),
  ]);
  return (config) => driver(config);
}

/** Respektiert prefers-reduced-motion (Spotlight-Bewegung ist JS-animiert,
 *  nicht per CSS abschaltbar). Guard für node/jsdom (kein matchMedia). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Baut die driver.js-Steps und **filtert Steps ohne vorhandenen Anker** raus
 * (Element nicht im DOM → Step übersprungen, kein leerer Spotlight/Crash).
 * Anker-freie Steps (Welcome) bleiben immer.
 */
export function buildTourSteps(
  defs: TourStepDef[],
  doc: Pick<Document, 'querySelector'> = document,
): DriveStep[] {
  return defs
    .filter((d) => !d.anchor || doc.querySelector(`[data-tour="${d.anchor}"]`) !== null)
    .map((d) => ({
      element: d.anchor ? `[data-tour="${d.anchor}"]` : undefined,
      popover: {
        title: d.title,
        description: d.description,
        side: d.side,
        align: d.align,
      },
    }));
}

export interface StartTourOpts {
  /** Default = ORIENTATION_STEPS. */
  steps?: TourStepDef[];
  /** Caller-Hook bei Tour-Ende (Finish/Skip/Esc/Overlay). NICHT von startTour selbst. */
  onEnd?: () => void;
  factory?: DriverFactory;
  doc?: Pick<Document, 'querySelector'>;
  /** Fortschritts-Text „1 / N". Default true (Mehr-Schritt-Tour); Single-Step-Tipps: false. */
  showProgress?: boolean;
}

/**
 * Startet die Tour. Gibt `false` zurück, wenn nach dem Anker-Filter KEIN Step
 * übrig ist (Caller kann dann später erneut versuchen). Ruft `onEnd` via
 * driver `onDestroyed` (deckt Finish, Überspringen, Esc, Overlay-Klick ab).
 */
export function startTour(opts: StartTourOpts = {}): boolean {
  const steps = buildTourSteps(opts.steps ?? ORIENTATION_STEPS, opts.doc);
  if (steps.length === 0) return false;

  const run = (factory: DriverFactory): void => {
    const instance = factory({
      showProgress: opts.showProgress ?? true,
      animate: !prefersReducedMotion(),
      steps,
      popoverClass: 'gf-tour-popover',
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      progressText: '{{current}} / {{total}}',
      onDestroyed: () => {
        opts.onEnd?.();
      },
    });
    instance.drive();
  };

  if (opts.factory) {
    run(opts.factory);
  } else {
    // Anker-Check oben bleibt synchron (Rückgabewert steuert die Caller-Retries);
    // nur das Laden von driver.js ist asynchron. Ladefehler → keine Tour, kein
    // `onEnd` (sonst würde OnboardingTour das Onboarding fälschlich abschließen).
    void loadDefaultFactory()
      .then(run)
      .catch((err) => {
        console.error('[onboarding] driver.js load failed:', err);
      });
  }
  return true;
}
